import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import Layout from '../components/Layout';
import { storiesData } from '../data/stories';
import { useSpeech } from '../hooks/useSpeech';
import { ArrowLeft, ArrowRight, Play, Square, Moon, Sun } from 'lucide-react';
import './StoryDetail.css';

const StoryDetail = () => {
    const { id } = useParams();
    const history = useHistory();
    const { cancel, speak } = useSpeech();
    const [activeIndex, setActiveIndex] = useState(-1);
    const [isPlaying, setIsPlaying] = useState(false);
    const [bedtimeMode, setBedtimeMode] = useState(false);
    const utteranceRef = useRef(null);

    const storyIndex = storiesData.findIndex(s => s.id === id);
    const story = storiesData[storyIndex];

    useEffect(() => {
        return () => {
            cancel();
            setIsPlaying(false);
        };
    }, [id, cancel]);

    if (!story) return <Layout title="Error">Story not found</Layout>;

    const handleNext = () => {
        const nextIndex = (storyIndex + 1) % storiesData.length;
        history.push(`/stories/${storiesData[nextIndex].id}`);
    };

    const handlePrev = () => {
        const prevIndex = (storyIndex - 1 + storiesData.length) % storiesData.length;
        history.push(`/stories/${storiesData[prevIndex].id}`);
    };

    const startReading = () => {
        if (!window.speechSynthesis) return;

        cancel();
        setIsPlaying(true);

        const fullText = story.content.join(' ');
        const utterance = new SpeechSynthesisUtterance(fullText);

        utterance.rate = bedtimeMode ? 0.7 : 0.85;
        utterance.pitch = bedtimeMode ? 0.9 : 1.1;

        const voices = window.speechSynthesis.getVoices();
        const preferredVoice = voices.find(v => v.name.includes('Samantha') || v.name.includes('Google US English'));
        if (preferredVoice) utterance.voice = preferredVoice;

        const paraOffsets = [];
        let cumulative = 0;
        story.content.forEach((para) => {
            paraOffsets.push({
                start: cumulative,
                end: cumulative + para.length + 1
            });
            cumulative += para.length + 1;
        });

        utterance.onboundary = (event) => {
            if (event.name === 'word') {
                const charIndex = event.charIndex;
                const index = paraOffsets.findIndex(offset => charIndex >= offset.start && charIndex < offset.end);
                if (index !== -1) {
                    setActiveIndex(index);
                }
            }
        };

        utterance.onend = () => {
            setIsPlaying(false);
            setActiveIndex(-1);
        };

        utteranceRef.current = utterance;
        window.speechSynthesis.speak(utterance);
    };

    const stopReading = () => {
        cancel();
        setIsPlaying(false);
        setActiveIndex(-1);
    };

    const toggleBedtimeMode = () => {
        setBedtimeMode(!bedtimeMode);
        if (isPlaying) {
            // Briefly stop and restart to apply new rate/pitch if desired, 
            // but for now just let it continue with existing settings or 
            // user can manually restart.
        }
    };

    const handleParagraphClick = (index, text) => {
        setActiveIndex(index);
        speak(text);
    };

    const renderMarkdown = (text) => {
        // Split text into segments of plain text, bold, and italics
        // Group 1: Bold (**bold**) or Italic (*italic*)
        const parts = text.split(/(\*\*.*?\*\*|\*.*?\*)/g);

        return parts.map((part, i) => {
            if (part.startsWith('**') && part.endsWith('**')) {
                return <strong key={i}>{part.slice(2, -2)}</strong>;
            } else if (part.startsWith('*') && part.endsWith('*')) {
                return <em key={i}>{part.slice(1, -1)}</em>;
            }
            return part;
        });
    };

    return (
        <Layout title={story.title} onBack={() => history.push('/stories')}>
            <div key={id} className={`story-detail-container ${bedtimeMode ? 'bedtime-mode' : ''}`} style={{ backgroundColor: bedtimeMode ? '' : story.color }}>

                {bedtimeMode && [1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                    <div key={i} className="floating-element star" style={{
                        left: `${(i * 13) % 100}%`,
                        top: `${(i * 7) % 80}%`,
                        animationDelay: `${i * 0.4}s`
                    }}>⭐</div>
                ))}

                <div className="bedtime-toggle" onClick={toggleBedtimeMode}>
                    {bedtimeMode ? <Sun size={20} /> : <Moon size={20} />}
                    {bedtimeMode ? 'Day Mode' : 'Bedtime Mode'}
                </div>

                <div className="story-card">
                    <div style={{ fontSize: '4rem', marginBottom: '10px' }}>{story.emoji}</div>

                    <button
                        className="story-action-button"
                        onClick={isPlaying ? stopReading : startReading}
                        style={{ margin: '0 auto 20px' }}
                    >
                        {isPlaying ? <><Square size={24} fill="currentColor" /> Stop Reading</> : <><Play size={24} fill="currentColor" /> Read Story</>}
                    </button>

                    <div className="story-content">
                        {story.content.map((para, index) => (
                            <div
                                key={index}
                                className={`story-paragraph ${index === activeIndex ? 'active' : ''}`}
                                onClick={() => handleParagraphClick(index, para)}
                            >
                                {renderMarkdown(para)}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="controls-container">
                    <button className="nav-arrow" onClick={handlePrev}><ArrowLeft size={32} /></button>
                    <button className="nav-arrow" onClick={handleNext}><ArrowRight size={32} /></button>
                </div>
            </div>
        </Layout>
    );
};

export default StoryDetail;
